import participantCss from "@countdown/theme/styles.css?inline";
import { type ReactElement, type ReactNode, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Document of the frame. The doctype keeps it out of quirks mode; a `srcdoc` document inherits
 * the backoffice's address, so the fonts load as they do here. The accent bar of both
 * stylesheets (`body::before`, `.countdown-page::before`) is hidden: the preview shows the card,
 * not the page around it. `overflow:hidden` avoids a scrollbar flash that would narrow the frame.
 */
const FRAME_HTML =
  '<!doctype html><html lang="de"><head><style>html{overflow:hidden}body::before{display:none!important}</style></head><body></body></html>';

/**
 * A piece of the participant page, drawn with the participant stylesheet in a same-origin
 * iframe, after the portal's `PreviewFrame`. The frame keeps the page's styles away from the
 * backoffice and the backoffice's away from the page, and its width is the page's width.
 *
 * React keeps one tree: state and events cross into the frame (react-dom listens on every
 * portal container). The backoffice's styles are copied in first, for the editing controls
 * that stand in the frame; the participant stylesheet comes last, as on the page, and the body
 * carries the page's class without a seasonal world. Inside the frame only native controls:
 * Radix portals would open in the backoffice page.
 *
 * One difference stays: the brand colour. The page sets it from the action's brand after the
 * contrast check; the backoffice does not load it, so the frame shows the house default of
 * the stylesheet (D35).
 *
 * The frame grows with its content. The mount is measured, not the body: the page's body has a
 * minimum height of the viewport, which would feed the frame its own height.
 *
 * With `page` the frame draws a whole page instead (the design tab, D49): the children bring
 * their own shell and card, and the body carries what `BaseLayout.astro` puts on it, the
 * seasonal world, the frame's look (`data-embed*`) and the night's colours.
 */
export interface FramePage {
  /** The body's data attributes by their dataset name; `undefined` leaves one off. */
  readonly data: Readonly<Record<string, string | undefined>>;
  /** The body's inline style, as `BaseLayout.astro` writes it. */
  readonly style?: string;
}

export function ParticipantFrame({
  title,
  page,
  children,
}: {
  readonly title: string;
  readonly page?: FramePage;
  readonly children: ReactNode;
}): ReactElement {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [mount, setMount] = useState<HTMLDivElement | null>(null);
  const [height, setHeight] = useState(480);

  const onLoad = (): void => {
    const doc = frameRef.current?.contentDocument;
    // Once per document: a second load event (or a stale handler) must not mount twice.
    if (doc === null || doc === undefined || doc.body.classList.contains("countdown-page")) {
      return;
    }
    document.head.querySelectorAll('style, link[rel="stylesheet"]').forEach((node) => {
      doc.head.append(node.cloneNode(true));
    });
    const page = doc.createElement("style");
    page.textContent = participantCss;
    doc.head.append(page);
    doc.body.className = "countdown-page";
    doc.body.dataset.seasonalWorld = "none";
    const root = doc.createElement("div");
    // Room for the card's shadow under it.
    root.style.paddingBottom = "16px";
    doc.body.append(root);
    setMount(root);
  };

  // The page's body follows the draft on every render; the attributes are a few strings.
  useEffect(() => {
    const body = mount?.ownerDocument.body;
    if (body === undefined || page === undefined) {
      return;
    }
    for (const [key, value] of Object.entries(page.data)) {
      if (value === undefined) {
        delete body.dataset[key];
      } else {
        body.dataset[key] = value;
      }
    }
    body.style.cssText = page.style ?? "";
  });

  useEffect(() => {
    const ResizeObserverOfFrame = mount?.ownerDocument.defaultView?.ResizeObserver;
    if (mount === null || ResizeObserverOfFrame === undefined) {
      return undefined;
    }
    const observer = new ResizeObserverOfFrame(() => {
      setHeight(mount.offsetHeight);
    });
    observer.observe(mount);
    return () => {
      observer.disconnect();
    };
  }, [mount]);

  return (
    <>
      <iframe
        ref={frameRef}
        title={title}
        srcDoc={FRAME_HTML}
        onLoad={onLoad}
        className="block w-full border-0"
        style={{ height }}
      />
      {mount === null
        ? null
        : createPortal(
            page === undefined ? (
              <div className="countdown-shell">
                <div className="countdown-card">
                  <main className="countdown-main">{children}</main>
                </div>
              </div>
            ) : (
              children
            ),
            mount,
          )}
    </>
  );
}
